import React, { ButtonHTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'ghost' | 'outline'
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  isLoading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({
    variant = 'primary',
    size = 'md',
    isLoading = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    children,
    className,
    disabled,
    ...props
  }, ref) => {
    const baseClasses = [
      'inline-flex items-center justify-center',
      'font-medium rounded-md',
      'focus:outline-none focus:ring-2 focus:ring-offset-2',
      'transition-all duration-200',
      'disabled:cursor-not-allowed disabled:opacity-50'
    ]

    const variantClasses = {
      primary: [
        'bg-primary-600 text-white',
        'hover:bg-primary-700 focus:ring-primary-500',
        'border border-transparent'
      ],
      secondary: [
        'bg-white text-gray-700 border border-gray-300',
        'hover:bg-gray-50 focus:ring-primary-500'
      ],
      success: [
        'bg-success-600 text-white',
        'hover:bg-success-700 focus:ring-success-500',
        'border border-transparent'
      ],
      danger: [
        'bg-danger-600 text-white',
        'hover:bg-danger-700 focus:ring-danger-500',
        'border border-transparent'
      ],
      warning: [
        'bg-warning-600 text-white',
        'hover:bg-warning-700 focus:ring-warning-500',
        'border border-transparent'
      ],
      ghost: [
        'bg-transparent text-gray-600',
        'hover:bg-gray-100 focus:ring-gray-500',
        'border border-transparent'
      ],
      outline: [
        'bg-transparent text-gray-700 border-gray-300',
        'hover:bg-gray-50 focus:ring-gray-500',
        'border'
      ]
    }

    const sizeClasses = {
      xs: 'px-2.5 py-1.5 text-xs min-h-[36px]',
      sm: 'px-3 py-2 text-sm min-h-[40px]',
      md: 'px-4 py-2 text-sm min-h-[44px]',
      lg: 'px-6 py-3 text-base min-h-[48px]',
      xl: 'px-8 py-4 text-lg min-h-[52px]'
    }

    const widthClasses = fullWidth ? 'w-full' : ''

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        className={clsx(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          widthClasses,
          className
        )}
        {...props}
      >
        {isLoading && (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
        )}

        {leftIcon && !isLoading && (
          <span className="mr-2">{leftIcon}</span>
        )}

        {children}

        {rightIcon && !isLoading && (
          <span className="ml-2">{rightIcon}</span>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
